<template>
  <div v-if="flashDeal">
    <PriceDueToday v-if="shouldDisplayPriceDueToday" :price-due-today />

    <HighlightedPrice
      :price="discountedPrice"
      :price-attrs="{
        'data-qa': 'productpage-product-price',
        'data-test': 'productpage-product-price',
      }"
    >
      <template #label>
        <PriceMainLabel
          v-if="shoulDisplayPriceLabel"
          :buyback-offer
          class="text-static-success-hi"
          :is-from-google-shopping
          :mobile-plan-offer="mobilePlan"
        />
      </template>
    </HighlightedPrice>

    <FlashSalesTooltip
      v-if="shouldDisplayTooltip"
      class="relative z-[1]"
      :discount="flashDeal.discount"
    />

    <div class="flex flex-wrap items-center gap-x-8">
      <div v-if="shouldDisplayOriginalPrice" class="relative z-[1]">
        <OriginalPrice
          :is-discount-reassurance-displayed="Boolean(selectedOffer.discount)"
          :price-when-new="product.priceWhenNew"
        />
      </div>

      <YouSave
        v-if="shouldDisplayYouSave"
        :is-discount-reassurance-displayed="Boolean(selectedOffer.discount)"
        :mobile-plan
        :price="discountedPrice"
        :price-when-new="product.priceWhenNew"
      />
    </div>

    <div class="flex flex-col flex-wrap">
      <FormattedMessage
        v-if="withAssuranceFee"
        class="caption mt-4"
        :definition="translations.qualityAssuranceFee"
      >
        <template #qualityAssuranceFeeLink>
          <ServiceFeeModal />
        </template>
      </FormattedMessage>
      <VatIncluded class="mt-4" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product.js'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-partnerships/mobile-plan/types/mobile-plan-offers.js'
import type { GetOfferV1Response } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/api/types'
import FlashSalesTooltip from '@backmarket/nuxt-layer-deals/FlashSalesTooltip.vue'
import HighlightedPrice from '@backmarket/nuxt-layer-deals/HighlightedPrice.vue'
import PriceDueToday from '@backmarket/nuxt-layer-deals/PriceDueToday.vue'
import { applyFlashSalesDiscount } from '@backmarket/nuxt-layer-deals/applyFlashSalesDiscount'
import ServiceFeeModal from '@backmarket/nuxt-layer-product-modals/ServiceFeeModal.vue'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import type { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep.js'

import translations from './Price.translations'
import OriginalPrice from './components/OriginalPrice/OriginalPrice.vue'
import PriceMainLabel from './components/PriceLabel/components/PriceMainLabel.vue'
import VatIncluded from './components/VatIncluded/VatIncluded.vue'
import YouSave from './components/YouSave/YouSave.vue'

const props = defineProps<{
  price: Price
  product: GetProductResponse
  selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
  buybackOffer?: GetOfferV1Response | null
  mobilePlan?: ReadonlyObjectDeep<MobilePlanOffer>
  withAssuranceFee?: boolean
  isFromGoogleShopping?: boolean
  variant?: 'default' | 'compact'
  withoutPriceDueToday?: boolean
}>()

const flashDeal = computed(() => {
  const deal = props.selectedOffer.deals?.[0] ?? null

  if (deal?.type !== 'flash_sales') return null

  return deal
})

const discountedPrice = computed(() => {
  if (props.price && flashDeal.value?.discount) {
    return applyFlashSalesDiscount(props.price, flashDeal.value.discount)
  }

  return props.price
})

const priceDueToday = computed(() => {
  if (props.selectedOffer.price && flashDeal.value?.discount) {
    return applyFlashSalesDiscount(
      props.selectedOffer.price,
      flashDeal.value.discount,
    )
  }

  return null
})

const hasBuybackOfferOrMobilePlan = computed(
  () => props.buybackOffer || props.mobilePlan,
)

const shouldDisplayPriceDueToday = computed(
  () => !props.withoutPriceDueToday && props.buybackOffer,
)

const shoulDisplayPriceLabel = computed(
  () => hasBuybackOfferOrMobilePlan.value || props.variant === 'compact',
)

const shouldDisplayTooltip = computed(() => props.variant !== 'compact')

const shouldDisplayOriginalPrice = computed(
  () => props.product.type !== 'Brand new' && !props.buybackOffer,
)

const shouldDisplayYouSave = computed(
  () =>
    shouldDisplayOriginalPrice.value &&
    discountedPrice &&
    props.variant === 'compact',
)
</script>
